import React, { useState } from "react"
import PropTypes from "prop-types"
import { CdnIntegrationsList } from "@modules/cdn/shared/components/CdnIntegrationsList"
import { CdnIntegrationModal } from "@modules/cdn/shared/components/CdnIntegrationModal"
import { CdnDeleteModal } from "@modules/cdn/shared/components/CdnDeleteModal"
import {
  SettingsBlock,
  SettingsBlockContent,
  SettingsCardSection,
} from "@modules/site/settings"
import { useSupportedCdnIntegrationsQuery } from "@modules/cdn/shared/queries.generated"
import { useSitePermissionsFieldsFragment } from "@modules/site/shared/fragments.generated"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useFlags } from "@modules/featureFlags"
import { GatsbyCloudCdn } from "./GatsbyCloudCdn"

const propTypes = {
  siteId: PropTypes.string.isRequired,
  siteName: PropTypes.string.isRequired,
}

const integrationTargetWrapperCss = {
  minHeight: `30rem`,
  marginBottom: `5rem`,
}

export function IntegrationTargetSection({ siteId, siteName }) {
  const { flags } = useFlags()
  const [selectedCdnToEdit, setSelectedCdnToEdit] = useState()
  const [selectedCdnToRemove, setSelectedCdnToRemove] = useState()
  const {
    data: cdnIntegrationsData,
    loading,
    error,
    refetch,
  } = useSupportedCdnIntegrationsQuery({
    variables: { siteId },
  })
  const { data: permissionsData } = useSitePermissionsFieldsFragment(siteId)

  if (loading) {
    return <div data-testid="cdn-list-loading">Loading</div>
  }

  const cdnIntegrations = cdnIntegrationsData?.supportedCDNIntegrations ?? []

  return (
    <React.Fragment>
      {flags.gatsbyHosting && (
        <GatsbyCloudCdn siteId={siteId} siteName={siteName} />
      )}
      <SettingsBlock
        id="deploy-targets"
        data-cy="deploy-targets-card"
        title="Hosting integrations"
        description="Gatsby Cloud can automatically deploy each site build to a CDN host of your choice—just connect and you are good to go!"
        css={integrationTargetWrapperCss}
      >
        <SettingsBlockContent>
          {error ? (
            <SettingsCardSection applyPaddingTop={false}>
              <ErrorAlert>{error.message}</ErrorAlert>
            </SettingsCardSection>
          ) : (
            <CdnIntegrationsList
              siteId={siteId}
              integrations={cdnIntegrations}
              onSelect={setSelectedCdnToEdit}
              onDelete={setSelectedCdnToRemove}
              allowToEdit={permissionsData?.permissions?.sites?.edit}
            />
          )}
        </SettingsBlockContent>
      </SettingsBlock>

      {selectedCdnToEdit && (
        <CdnIntegrationModal
          siteId={siteId}
          siteName={siteName}
          cdn={selectedCdnToEdit}
          onClose={() => setSelectedCdnToEdit(undefined)}
          onConnect={refetch}
        />
      )}

      {selectedCdnToRemove && (
        <CdnDeleteModal
          cdn={selectedCdnToRemove}
          siteName={siteName}
          siteId={siteId}
          onClose={() => setSelectedCdnToRemove(undefined)}
        />
      )}
    </React.Fragment>
  )
}

IntegrationTargetSection.propTypes = propTypes
